import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import SharedForm from '../../../components/ui/Form';
import { AppLoadContext } from '../../../components/ui/AppLoadContext';
import {
    AG_MEETING_STATUS_INVERSE,
    AG_STATUS,
    ALERT_TYPE,
    ALIGN,
    DOCUMENT_FORMAT,
    FORM_ACTION_TYPE,
    MIME_TYPES,
    POSITIONS, RECORD_STATUS_ABBR, VALIDATIONS,
} from '../../../constants';
import {
    createDocument,
    getDocumentById,
    getDocumentsByCompanyId,
    resetDocument,
    updateDocument,
} from '../../../ducks/document';
import { AlertContext } from '../../../context/AlertContext';
import messages from '../../../utils/helper/messages';
import FileProgress from '../../../components/ui/FileProgress';
import {
    checkObjectsEqual,
    dataURLtoFile,
    dropdownValues,
    encodeSpecialChars,
    getDropdownPlaceholder,
    getFileExtension,
    getNameOptions,
} from '../../../utils/utils';
import {
    getAdvisoryGroupData,
    getAdvisoryGroupMeetingRelatedData,
    getMeetingDetailsByAgId, resetAdvisoryGroupMeetingRelatedData,
} from '../../../ducks/advisoryGroup';
import Loader from '../../../components/ui/Loader';

const AddDocumentModal = ({ show, data = null, action, onCancel }) => {
    const dispatch = useDispatch();
    const isAppLoaded = useContext(AppLoadContext);
    const { showAlert } = useContext(AlertContext);

    const currentUser = useSelector(state => state.user.currentUser);
    const { masterList: advisoryGroups, agMemberMeetingDetails: agMeetings, meetingData: agMeetingData } = useSelector(state => state.advisoryGroup);
    const { currentDocument, documentContentMap, typeSubTypeMap, subTypes } = useSelector(state => state.document);

    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const progressRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [documentDetails, setDocumentDetails] = useState(null);

    // Dropdown options
    const [agOptions, setAgOptions] = useState(null);
    const [meetingOptions, setMeetingOptions] = useState([getDropdownPlaceholder('Select Meeting')]);
    const [memberOptions, setMemberOptions] = useState([getDropdownPlaceholder('Select Member')]);
    const [locationOptions, setLocationOptions] = useState([getDropdownPlaceholder('Select Location')]);
    const [contentTypeOptions, setContentTypeOptions] = useState([getDropdownPlaceholder('Select Content Type')]);
    const [contentSubTypeOptions, setContentSubTypeOptions] = useState([getDropdownPlaceholder('Select Content Sub-type')]);
    const [documentOptions, setDocumentOptions] = useState([getDropdownPlaceholder('Select Document Name')]);

    const arr = [
        { key: 'contentType', labelName: 'Content Type', type: 'options', optionValues: contentTypeOptions },
        { key: 'contentSubType', labelName: 'Content Sub-type', type: 'options', optionValues: contentSubTypeOptions, disabled: ['', undefined].includes(documentDetails?.contentType) },
        { key: 'documentContentMappingId', labelName: 'Document Name', type: 'options', optionValues: documentOptions },
        { key: 'description', labelName: 'Document Description', type: 'textarea', maxLength: 255, required: false, valid: VALIDATIONS.String.noSpecialChars, charCounter: true },
        { key: 'agId', labelName: 'Advisory Group', type: 'options', optionValues: agOptions },
        { key: 'meetingId', labelName: 'AG Meeting Title', type: 'options', optionValues: meetingOptions, disabled: !documentDetails?.agId || meetingOptions.length === 1, required: false },
        { key: 'agMemberId', labelName: 'AG Member', type: 'options', optionValues: memberOptions, disabled: !documentDetails?.meetingId || memberOptions.length === 1, required: false },
        { key: 'locationId', labelName: 'Meeting Location', type: 'options', optionValues: locationOptions, disabled: !documentDetails?.meetingId || locationOptions.length === 1, required: false },
        {
            key: 'document', labelName: 'Document Upload', type: 'file',
            fileNameKey: 'fileName',
            fileURLKey: 'document',
            allowedTypes: [MIME_TYPES.PDF, MIME_TYPES.JPG, MIME_TYPES.DOCX, MIME_TYPES.PPTX, MIME_TYPES.XLSX]
        },
    ];

    const updateSubTypeOptions = (contentType) => {
        const subTypeOptions = typeSubTypeMap[contentType]?.map(type => ({ id: type, value: type })) ?? [];
        setContentSubTypeOptions([
            getDropdownPlaceholder('Select Content Sub-type'),
            ...subTypeOptions
        ]);
    }

    const updateDocumentOptions = (data) => {
        let docNames = [];
        let docNameOptions = [];
        if (data.length) {
            for (let item of data) {
                if (!docNames.includes(item.documentType)) {
                    docNames.push(item.documentType);
                    docNameOptions.push({ id: item.id, value: item.documentType });
                }
            }
            setDocumentOptions([
                getDropdownPlaceholder('Select Document Name'),
                ...docNameOptions
            ]);
        } else {
            const docOptions = dropdownValues(documentContentMap, 'documentType', 'id', true, 'Select Document Name');
            setDocumentOptions(docOptions);
            handleDocumentDetailsChange({
                documentContentMappingId: '',
            });
        }
    }

    const inputChange = (name, value) => {
        if (name === "document") {
            let fileExtension = getFileExtension(value.name).toUpperCase();
            fileExtension = (['JPG', 'JPEG', 'JFIF'].includes(fileExtension) ? 'JPEG' : fileExtension);
            handleDocumentDetailsChange({
                fileName: value.name,
                document: value,
                format: Object.keys(DOCUMENT_FORMAT).find(type => DOCUMENT_FORMAT[type] === fileExtension)
            });
            setFile(value);
        } else if (name === 'contentType') {
            // filter/reset content subtype and document name based on content type
            handleDocumentDetailsChange({
                [name]: value,
            });
            updateSubTypeOptions(value);
            const filterData = documentContentMap?.filter(item => item.contentType === value);
            updateDocumentOptions(filterData);
        } else if (name === 'contentSubType') {
            // filter/reset document name based on content sub type
            handleDocumentDetailsChange({
                [name]: value,
            });
            let filterData = [];
            if (documentDetails.contentType !== '') {
                filterData = documentContentMap?.filter(item => (item.contentType === documentDetails.contentType && item.contentSubType === value));
            }
            updateDocumentOptions(filterData);
        } else if (name === 'documentContentMappingId') {
            // automatically set content type and subtype based on document name
            const parsedValue = parseInt(value);
            const docType = documentContentMap?.find(dcm => dcm.id === parsedValue);
            updateSubTypeOptions(docType.contentType);
            handleDocumentDetailsChange({
                [name]: parsedValue,
                documentType: docType.documentType,
                contentType: docType.contentType,
                contentSubType: docType.contentSubType,
            });
        } else if (name === 'agId') {
            // reset dropdowns based on agId after AG change
            handleDocumentDetailsChange({
                [name]: parseInt(value),
                meetingId: null,
                agMemberId: null,
                locationId: null,
            });
        } else if (name === 'meetingId') {
            // reset dropdowns based on meetingId after meeting change
            handleDocumentDetailsChange({
                [name]: parseInt(value),
                agMemberId: null,
                locationId: null,
            });
        } else {
            handleDocumentDetailsChange({ [name]: value });
        }
    }

    const handleDocumentDetailsChange = (update) => {
        setDocumentDetails((details) => ({ ...details, ...update }))
    }

    const handleSubmit = () => {
        setIsLoading(true);
        const document = dataURLtoFile(documentDetails.document, documentDetails.fileName ?? null);

        const commonData = {
            documentContentMappingId: documentDetails.documentContentMappingId,
            description: encodeSpecialChars(documentDetails.description) ?? null,
            agId: documentDetails.agId !== '' ? documentDetails.agId : null,
            meetingId: documentDetails.meetingId !== '' ? documentDetails.meetingId : null,
            agMemberId: documentDetails.agMemberId !== '' ? documentDetails.agMemberId : null,
            locationId: documentDetails.locationId !== '' ? documentDetails.locationId : null,
            modifiedBy: currentUser?.id,
            document: document,
        }

        if (action === FORM_ACTION_TYPE.New) {
            const addData = {
                ...documentDetails,
                ...commonData,
                companyId: currentUser?.companyId,
                createdBy: currentUser?.id,
                status: RECORD_STATUS_ABBR.Operational,
            }
            dispatch(createDocument(addData, handleUpload, handleSuccess, handleError));
        } else if (action === FORM_ACTION_TYPE.Edit) {
            const updateData = {
                ...documentDetails,
                ...commonData,
                id: data.id,
                companyId: data.companyId,
                document: file ? document : null,
            }
            dispatch(updateDocument(updateData, handleUpload, handleSuccess, handleError));
        }
    }

    const handleModalHide = () => {
        setDocumentDetails(null);
        setFile(null);
        setAllOptionsNull();
        dispatch(resetAdvisoryGroupMeetingRelatedData());
        dispatch(resetDocument());
        onCancel();
    }

    const setAllOptionsNull = () => {
        setMeetingOptions(null);
        setMemberOptions(null);
        setLocationOptions(null);
    }

    const handleSuccess = () => {
        showAlert(action === FORM_ACTION_TYPE.New ? messages.success.addDocument : messages.success.updateDocument, ALERT_TYPE.SUCCESS);
        setIsLoading(false);
        setProgress(0);
        handleModalHide();
        dispatch(getDocumentsByCompanyId(currentUser?.companyId));
    }

    const handleError = (error) => {
        setIsLoading(false);
        setProgress(0)
        showAlert(`${messages.error.prefix} ${error}`, ALERT_TYPE.ERROR);
    }

    const handleUpload = (event) => {
        progressRef.current = Math.round(
            (100 * event.loaded) / event.total
        );
        setProgress(progressRef.current);
    }

    const isValidMeetingInfo = () => {
        return !checkObjectsEqual(data, documentDetails, ['document']);
    }

    useEffect(() => {
        if (isAppLoaded) {
            if (!currentUser) {
                return;
            }
            setIsDataLoading(true);
            dispatch(getAdvisoryGroupData(currentUser?.companyId, () => {
                setIsDataLoading(false);
            }, (error) => {
                showAlert(`${messages.error.prefix} ${error}.`, ALERT_TYPE.ERROR);
                setIsLoading(false);
            }));
            if (action === FORM_ACTION_TYPE.Edit) {
                setIsDataLoading(true);
                dispatch(getDocumentById(currentUser?.companyId, data.id, () => {
                    setIsDataLoading(false);
                }, (error) => {
                    showAlert(`${messages.error.prefix} ${error}`, ALERT_TYPE.ERROR);
                }));
            }
        }
    }, [isAppLoaded, currentUser]);

    useEffect(() => {
        if (documentContentMap) {
            const typeOptions = Object.keys(typeSubTypeMap)?.map(type => ({ id: type, value: type }));
            setContentTypeOptions([
                getDropdownPlaceholder('Select Content Type'),
                ...typeOptions
            ]);

            const subTypeOptions = subTypes?.map(type => ({ id: type, value: type }));
            setContentSubTypeOptions([
                getDropdownPlaceholder('Select Content Sub-type'),
                ...subTypeOptions
            ]);

            const docOptions = dropdownValues(documentContentMap, 'documentType', 'id', true, 'Select Document Name');
            setDocumentOptions(docOptions);
        }
    }, [documentContentMap]);

    useEffect(() => {
        if (data) {
            const editData = { ...data };

            if (action === FORM_ACTION_TYPE.Edit) {
                updateSubTypeOptions(editData.contentType);
                const filterData = documentContentMap?.filter(item => (item.contentType === editData.contentType && item.contentSubType === editData.contentSubType));
                updateDocumentOptions(filterData);
            }

            setDocumentDetails(editData);
        }
    }, [data]);

    useEffect(() => {
        if (advisoryGroups) {
            setAgOptions(dropdownValues(advisoryGroups?.filter(ag => ag.status === AG_STATUS.Active || ag.status === AG_STATUS.New),
                'shortName', undefined, documentDetails?.agId === undefined, 'Select AG'));
        }
    }, [advisoryGroups]);

    useEffect(() => {
        if (documentDetails) {
            const { agId, meetingId } = documentDetails;

            if (agId) {
                dispatch(getMeetingDetailsByAgId(agId))
            }
            if (agId && meetingId) {
                dispatch(getAdvisoryGroupMeetingRelatedData(agId, meetingId));
            }
        }
    }, [documentDetails]);

    useEffect(() => {
        if (agMeetings) {
            const validMeetings = agMeetings.filter(meeting => meeting.status !== AG_MEETING_STATUS_INVERSE.Cancelled);
            setMeetingOptions(dropdownValues(validMeetings, 'title', undefined, true, 'Select Meeting'));
            // Reset and remove old options on AG or meeting change
            setMemberOptions([getDropdownPlaceholder('Select Member')]);
            setLocationOptions([getDropdownPlaceholder('Select Location')]);
        }
    }, [agMeetings]);

    useEffect(() => {
        if (agMeetingData) {
            const { members, locations } = agMeetingData;

            let memOptions = getNameOptions(members, 'agMemberId', true, 'Select Member');
            setMemberOptions(memOptions);

            const locOptions = dropdownValues(locations, 'name', 'id', true, 'Select Location');
            setLocationOptions(locOptions);
        }
    }, [agMeetingData]);

    useEffect(() => {
        if (currentDocument) {
            handleDocumentDetailsChange({ document: currentDocument.documentFile });
        }
    }, [currentDocument]);

    return (
        <Modal
            show={show}
            fullscreen={false}
            backdrop="static"
            onHide={handleModalHide}
            size="md">
            <Modal.Header closeButton>
                <Modal.Title>{action === FORM_ACTION_TYPE.New ? 'Upload New' : 'Edit'} Document</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="px-xl-2 py-xl-2">
                    {isDataLoading && (<Loader />)}
                    <SharedForm
                        cols={12}
                        array={arr}
                        modelObj={documentDetails}
                        loading={isLoading}
                        onInputChanged={inputChange}
                        onSubmit={handleSubmit}
                        onCancel={handleModalHide}
                        actionBarPosition={POSITIONS.TOP}
                        actionBarAlign={ALIGN.RIGHT}
                        disabled={!isValidMeetingInfo()}
                    />
                    {(progress > 0 && file) && (
                        <FileProgress
                            file={file}
                            progress={progress}
                            wrapperStyle="mt-2"
                        />
                    )}
                </div>
            </Modal.Body>
        </Modal>
    )
}

export default AddDocumentModal;
